<script setup lang="ts">
import type { Product } from '~/types'

const props = defineProps<{
  product: CampaignProduct | null
  listIndex?: number
}>()

// GA4 `index` is 1-based (position in the product list).
const trackingIndex = computed(() => (props.listIndex ?? 0) + 1)

const isAlternativeSheet = inject<boolean>('isAlternativeSheet', false)
const isHybridSearch = useIsHybridSearch()
const trackingPageSection = computed(() => (isAlternativeSheet ? 'cart_gpo_recommendation' : undefined))

const { isHumanMedicineRestricted } = useHumanMedicineRestriction()
const isScalePriceSheetOpen = ref(false)
const isOrderOptimizationOpen = ref(false)
const { currencyToString } = useHelper()
const pageSection = usePageSection()
const { trackAddToShoppingList, trackRemoveFromShoppingList, trackCreateCustomShoppingList } = useShoppingListTracking()
const { trackViewItem, getTrackingPriceEuros } = useProductTracking()
const gtm = useGtm()
const {
  quantity,
  reference,
  clinicChain,
  currentPrice,
  currentPriceCents,
  selectedProduct,
  isFavourite,
  isSubscribed,
  configurations,
  changeSelected,
  selectedProductSupplier,
  productDetailUrl,
  updateSelectedReferenceName,
} = useProductStateManagement(props.product!)

const {
  updateQuantity,
  updateReminder,
  faveUpdate,
} = useProductActionsManagement()

const isProductRestricted = computed(() =>
  isHumanMedicineRestricted(selectedProduct.value.saleDelimitations),
)

const handleFaveChange = async (value: boolean) => {
  const wasFavourite = isFavourite.value
  await faveUpdate(value, selectedProduct.value.handle, wasFavourite)
  isFavourite.value = value
  trackViewItem({
    product: selectedProduct.value,
    price: getTrackingPriceEuros(selectedProduct.value, currentPriceCents.value),
    selectedFields: selectedProduct.value.fields,
    supplierName: selectedProductSupplier.value?.name,
  })
  if (wasFavourite && !value) {
    trackRemoveFromShoppingList({
      product: { handle: selectedProduct.value.handle, name: props.product?.name },
      supplierName: selectedProductSupplier.value?.name,
      pageSection,
    })
  }
}

function handleSavedToList({ listName, isNewList, quantity }: { listName: string, isNewList: boolean, quantity: number }) {
  if (isNewList) {
    trackCreateCustomShoppingList({ pageSection })
  }
  const selectedProductWithName = { ...selectedProduct.value, name: props.product.name }
  trackAddToShoppingList({
    products: [selectedProductWithName],
    listName,
    fields: selectedProduct.value.fields,
    reference: reference.value,
    supplierName: selectedProductSupplier.value?.name,
    quantity,
    pageSection,
  })
}

function trackSelectProductVariant(variantType: string) {
  if (!selectedProduct.value) return
  gtm?.trackEvent({
    event: 'select_product_variant',
    page_section: trackingPageSection.value ?? pageSection,
    item_id: selectedProduct.value.handle,
    item_name: props.product?.name ?? '',
    supplier_name: selectedProductSupplier.value?.name ?? '',
    variant_type: variantType,
  })
}

function handleVariantChange(key: string, value: string) {
  changeSelected(key, value)
  trackSelectProductVariant(key)
}

function handleUpdateQuantity(value: number) {
  const success = updateQuantity(selectedProductSupplier.value?.handle ?? '', selectedProduct.value.handle, reference.value, value, quantity.value, { index: trackingIndex.value, pageSection: trackingPageSection.value })
  if (success) {
    quantity.value = value
  }
  trackViewItem({
    product: selectedProduct.value,
    price: getTrackingPriceEuros(selectedProduct.value, currentPriceCents.value),
    selectedFields: selectedProduct.value.fields,
    supplierName: selectedProductSupplier.value?.name,
    quantity: value,
  })
}

function handleScaleToCart(scaleQuantity: number) {
  const success = updateQuantity(selectedProductSupplier.value?.handle ?? '', selectedProduct.value.handle, reference.value, scaleQuantity, quantity.value, { index: trackingIndex.value, pageSection: trackingPageSection.value })
  if (success) {
    quantity.value = scaleQuantity
  }
  isScalePriceSheetOpen.value = false
  trackViewItem({
    product: selectedProduct.value,
    price: getTrackingPriceEuros(selectedProduct.value, currentPriceCents.value),
    selectedFields: selectedProduct.value.fields,
    supplierName: selectedProductSupplier.value?.name,
    quantity: scaleQuantity,
  })
}

function handleUpdateReminder(value: boolean) {
  updateReminder(selectedProduct.value.handle, !value)
  isSubscribed.value = !isSubscribed.value
}

// necessary to display the supplier data in the add to favorite dialog
function mergeSupplierToProduct(product: Product) {
  return {
    ...product,
    supplier: selectedProductSupplier.value,
  }
}

const hasReachedScalePrices = computed(() => {
  return selectedProduct.value?.scalePrices?.some((scale) => {
    if (quantity.value === 0) {
      return scale.minQuantity === 1
    }
    return quantity.value >= scale.minQuantity
  })
})

// TODO: Remove this once we replace the old search product data with the new one
function campaignPrices(campaign: Campaign) {
  if (!campaign) return undefined
  return {
    ...campaign,
    beforePrice: currencyToString(campaign?.beforePrice),
    afterPrice: currencyToString(campaign?.afterPrice),
  }
}
</script>

<template>
  <vmz-card flat class="pt-5 pb-7 px-5">
    <v-row dense>
      <v-col
        cols="12"
        sm="3"
        md="2"
        lg="2"
        data-testid="action-buttons"
      >
        <VmzProductImage
          min-height="180"
          max-height="180"
          :scale-pricing="'campaign'"
          :is-favourite="isFavourite"
          :img-src="selectedProduct.imageUrl"
          :alt="product.name"
          :fallback-src="selectedProductSupplier?.imageUrl"
          :navigation-path="productDetailUrl"
          :product="mergeSupplierToProduct(selectedProduct)"
          :current-price="currentPrice"
          :campaign="selectedProduct?.campaign"
          @update:is-favourite="(value) => handleFaveChange(value)"
          @saved="handleSavedToList"
        />
      </v-col>
      <v-col
        cols="12"
        sm="9"
        md="3"
        lg="4"
      >
        <v-row class="h-100" no-gutters>
          <v-col cols="12" class="mb-2">
            <VmzProductHeadlineAndInfo
              :clinic-chain="clinicChain"
              :id-external="selectedProduct.idExternal"
              :product-name="product.name"
              :manufacturer="selectedProduct.manufacturer"
              :supplier="selectedProductSupplier"
              :navigation-path="productDetailUrl"
            />
          </v-col>
          <v-col cols="12" class="d-flex flex-column mb-2">
            <VmzProductPriceAndScaleGroup
              :list-price="currencyToString(selectedProduct.listPrice)"
              :current-price="currentPrice"
              :campaign="campaignPrices(selectedProduct.campaign)"
              :discount-percent="selectedProduct.campaign?.discountPercent"
              :scale-pricing="selectedProduct.scalePricing"
              :has-scale-prices="selectedProduct.hasScalePrices"
              :reached-scale-prices="hasReachedScalePrices"
              :scale-prices="selectedProduct.scalePrices"
              :selected-quantity="quantity"
              :customer-reference="selectedProduct.references"
              :availability-status="selectedProduct.availability"
              :human-medicine-restricted="isProductRestricted"
              :max-quantity="selectedProduct.orderCondition?.max"
              @scale-prices-handler="isScalePriceSheetOpen = !isScalePriceSheetOpen"
              @select-scale-quantity="handleUpdateQuantity"
            />
          </v-col>
          <v-col cols="12" class="d-flex align-center vmz-product-info__third-col mt-auto">
            <VmzProductAvailabilityAndOrderOptimization
              :availability-best-before="selectedProduct.dateBestBefore ?? ''"
              :re-availability-date="selectedProduct.reAvailabilityDate ?? ''"
              :supplier-img-src="selectedProductSupplier?.imageUrl ?? ''"
              :supplier-name="selectedProductSupplier?.handle ?? ''"
              :availability-status="selectedProduct.availability"
              :last-ordered-quantity="selectedProduct.lastOrderedQuantity"
              @order-optimization-handler="isOrderOptimizationOpen = !isOrderOptimizationOpen"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        sm="12"
        md="7"
        lg="6"
      >
        <v-row class="h-100 ms-8" no-gutters>
          <v-col cols="12" class="mb-2 pb-1">
            <v-row no-gutters>
              <template v-for="(values, key) in configurations" :key="key">
                <v-col
                  cols="12"
                  sm="6"
                  lg="12"
                  class="mb-auto pb-2"
                >
                  <p class="text-caption text-vm-grey-7 mb-1">
                    {{ $t(`productGroup.${key}`) }}
                  </p>
                  <vmz-btn-toggle
                    class="flex-wrap overflow-visible h-auto vmz-btn-toggle"
                    variant="flat"
                    data-test-id="product-variant-toggle"
                    mandatory
                    shaped
                  >
                    <vmz-btn
                      v-for="value in sortVariants(values)"
                      :key="value"
                      class="rounded-lg vmz-btn-toggle-btn"
                      :active="selectedProduct.fields[key] === value"
                      data-testid="action-button"
                      data-track-id="product_select_variant"
                      @click="() => handleVariantChange(key, value)"
                    >
                      {{ value }}
                    </vmz-btn>
                  </vmz-btn-toggle>
                </v-col>
              </template>
            </v-row>
          </v-col>
          <v-col cols="12" class="d-flex justify-end mt-auto">
            <template v-if="selectedProduct.availability === 'NOT_AVAILABLE'">
              <VmzProductAlternativeAndReminder
                :reminder="isSubscribed"
                :alternatives="selectedProduct.alternatives"
                @update:reminder="handleUpdateReminder"
              />
            </template>
            <template v-else>
              <VmzProductReferenceAndQuantity
                :quantity-value="quantity"
                :last-ordered-quantity="selectedProduct.lastOrderedQuantity"
                :last-ordered-date="selectedProduct.lastOrderedDate"
                :selected-reference-name="reference"
                :customer-reference="selectedProduct.references"
                :alternatives="selectedProduct.alternatives"
                :order-condition="selectedProduct.orderCondition"
                :human-medicine-restricted="isProductRestricted"
                @update:quantity-value="handleUpdateQuantity"
                @update:selected-reference-name="updateSelectedReferenceName"
              />
            </template>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </vmz-card>
  <ProductScalePriceSheet
    v-if="!isHybridSearch"
    :is-scale-price-sheet-open="isScalePriceSheetOpen"
    :scale-prices="selectedProduct.scalePrices ?? []"
    :scale-add-to-cart="handleScaleToCart"
    :max-quantity="selectedProduct.orderCondition?.max"
    @update:is-scale-price-sheet-open="value => isScalePriceSheetOpen = value"
  />
  <VmzScaleSheet
    v-else
    :is-scale-price-sheet-open="isScalePriceSheetOpen"
    :scale-prices="selectedProduct.scalePrices ?? []"
    :scale-add-to-cart="async (qty: number) => handleScaleToCart(qty)"
    :max-quantity="selectedProduct.orderCondition?.max"
    @update:is-scale-price-sheet-open="value => isScalePriceSheetOpen = value"
  />
  <v-bottom-sheet v-model="isOrderOptimizationOpen">
    <ProductOrderOptimizationSheet
      :product="selectedProduct"
      :is-open="isOrderOptimizationOpen"
    />
  </v-bottom-sheet>
</template>

<style scoped lang="scss">
.vmz-btn-toggle {
  :deep(.v-btn--active) {
    background-color: rgb(var(--v-theme-vm-grey-2)) !important;
    color: rgb(var(--v-theme-vm-grey-9)) !important;
  }
  &-btn {
    :deep(.v-btn__overlay) {
      opacity: 0 !important;
    }
    :deep(.v-btn__content) {
      white-space: normal;
    }
    &:disabled {
      background-color: rgb(var(--v-theme-vm-grey-1)) !important;
      :deep(.v-btn__overlay) {
        opacity: 0 !important;
      }
      :deep(.v-btn__content) {
        color: rgb(var(--v-theme-vm-grey-4)) !important;
      }
    }
  }
}
</style>
